import { useEffect, useLayoutEffect } from "react";
import { create } from "zustand";
import {
  persist,
  createJSONStorage,
  type StateStorage,
} from "zustand/middleware";
import {
  emptyVector,
  PERSONAS,
  type Persona,
  type PersonaVector,
  type ResolvedPersona,
} from "./types";
import { classifyReferrer } from "./referrer";
import { routeTopicFor } from "./routeTopics";
import { track } from "../services/analytics/core";

// ─── Tuning ──────────────────────────────────────────────────────────────────
const REFERRER_WEIGHT = 3; // a known referring domain is a strong first-touch signal
const DWELL_WEIGHT_PER_SEC = 0.4; // reading a topic accrues slowly but surely
const PER_TOPIC_CAP = 12; // no single signal can dominate forever
const HALF_LIFE_MS = 14 * 24 * 60 * 60 * 1000; // stale interest fades over ~2 weeks
// A persona "wins" only with enough absolute weight AND enough share of the total.
const MIN_ABSOLUTE = 2;
const MIN_SHARE = 0.34;
// A decisive lead — required before the strongest adaptations (swapping the money CTA).
const STRONG_ABSOLUTE = 4;
const STRONG_SHARE = 0.55;
// Fraction of classified visitors held back on the DEFAULT experience, so lift is
// measurable (an honest A/B). Set to 0 to disable the holdout entirely.
const HOLDOUT_FRACTION = 0.15;

// ─── Privacy: honour Do-Not-Track / Global Privacy Control ───────────────────
function trackingAllowed(): boolean {
  if (typeof navigator === "undefined") return true;
  const nav = navigator as Navigator & {
    msDoNotTrack?: string;
    globalPrivacyControl?: boolean;
  };
  const win =
    typeof window !== "undefined"
      ? (window as Window & { doNotTrack?: string })
      : undefined;
  const dnt = nav.doNotTrack ?? win?.doNotTrack ?? nav.msDoNotTrack;
  if (dnt === "1" || dnt === "yes") return false;
  if (nav.globalPrivacyControl === true) return false;
  return true;
}

// In-memory fallback: used when tracking is disallowed (adapt within the session,
// persist nothing) or when localStorage is unavailable.
function memoryStorage(): StateStorage {
  const m = new Map<string, string>();
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  };
}

function pickStorage(): StateStorage {
  if (!trackingAllowed() || typeof localStorage === "undefined") {
    return memoryStorage();
  }
  try {
    const probe = "__wl_persona_probe__";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return memoryStorage();
  }
}

// ─── Decay + resolution ──────────────────────────────────────────────────────
export function decayVector(
  vector: PersonaVector,
  updatedAt: number | null,
): PersonaVector {
  if (!updatedAt) return vector;
  const elapsed = Date.now() - updatedAt;
  if (elapsed <= 0) return vector;
  const factor = Math.pow(0.5, elapsed / HALF_LIFE_MS);
  const out = emptyVector();
  for (const p of PERSONAS) out[p] = vector[p] * factor;
  return out;
}

export function resolvePersona(vector: PersonaVector): ResolvedPersona {
  return resolvePersonaTiered(vector).persona;
}

export interface PersonaTier {
  persona: ResolvedPersona;
  /** True only when the lead is decisive — gates the most aggressive adaptations. */
  confident: boolean;
}

/**
 * Resolve the persona and how sure we are. "Tentative" (passes the base thresholds)
 * enables soft adaptations; "confident" (a decisive lead) is required before swapping
 * the primary CTA away from the default. A 51/49 split stays tentative on purpose.
 */
export function resolvePersonaTiered(vector: PersonaVector): PersonaTier {
  let top: Persona = "writer";
  let total = 0;
  for (const p of PERSONAS) {
    total += vector[p];
    if (vector[p] > vector[top]) top = p;
  }
  if (total <= 0) return { persona: "unknown", confident: false };
  const value = vector[top];
  const share = value / total;
  if (value < MIN_ABSOLUTE || share < MIN_SHARE) {
    return { persona: "unknown", confident: false };
  }
  const confident = value >= STRONG_ABSOLUTE && share >= STRONG_SHARE;
  return { persona: top, confident };
}

// ─── Store ───────────────────────────────────────────────────────────────────
export type Variant = "adapted" | "control";

interface PersonaState {
  vector: PersonaVector;
  updatedAt: number | null;
  /** Stable A/B bucket, assigned once. "control" always sees the default experience. */
  variant: Variant | null;
  addSignal: (partial: Partial<PersonaVector>) => void;
  ensureVariant: () => void;
  reset: () => void;
}

export const usePersonaStore = create<PersonaState>()(
  persist(
    (set) => ({
      vector: emptyVector(),
      updatedAt: null,
      variant: null,
      addSignal: (partial) =>
        set((state) => {
          const next = { ...state.vector };
          for (const p of PERSONAS) {
            if (partial[p]) {
              next[p] = Math.min(
                PER_TOPIC_CAP,
                next[p] + (partial[p] as number),
              );
            }
          }
          return { vector: next, updatedAt: Date.now() };
        }),
      ensureVariant: () =>
        set((state) =>
          state.variant
            ? state
            : {
                variant:
                  Math.random() < HOLDOUT_FRACTION ? "control" : "adapted",
              },
        ),
      reset: () =>
        set({ vector: emptyVector(), updatedAt: null, variant: null }),
    }),
    {
      name: "writerslogic-visitor-persona",
      storage: createJSONStorage(pickStorage),
      partialize: (s) => ({
        vector: s.vector,
        updatedAt: s.updatedAt,
        variant: s.variant,
      }),
      // Fade stale interest at load so an old visit doesn't dominate a new one.
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.vector = decayVector(state.vector, state.updatedAt);
        }
      },
    },
  ),
);

// ─── Selector hooks ──────────────────────────────────────────────────────────
export function usePersonaVector(): PersonaVector {
  return usePersonaStore((s) => s.vector);
}

export interface PersonaDecision {
  /** Persona to render for — forced to "unknown" for the control group. */
  persona: ResolvedPersona;
  /** The true inferred persona regardless of A/B bucket, for analytics tagging. */
  inferred: ResolvedPersona;
  confident: boolean;
  variant: Variant;
}

let exposureLoggedFor = "";

/**
 * The single hook the UI uses. Resolves the persona with confidence, applies the A/B
 * holdout (control renders the default), and logs one exposure event per classified
 * visitor per session so lift is measurable in analytics — no new backend, no PII.
 */
export function usePersonaDecision(): PersonaDecision {
  const vector = usePersonaStore((s) => s.vector);
  const variant = usePersonaStore((s) => s.variant);
  const ensureVariant = usePersonaStore((s) => s.ensureVariant);
  const { persona: inferred, confident } = resolvePersonaTiered(vector);

  // Assign an A/B bucket the moment we first classify the visitor.
  useEffect(() => {
    if (inferred !== "unknown" && !variant) ensureVariant();
  }, [inferred, variant, ensureVariant]);

  // Log exposure once per (persona, variant) per session — the measurement denominator.
  useEffect(() => {
    if (inferred === "unknown" || !variant) return;
    const key = `${inferred}:${variant}`;
    if (exposureLoggedFor === key) return;
    exposureLoggedFor = key;
    track("persona_exposure", { persona: inferred, variant, confident });
  }, [inferred, variant, confident]);

  const isControl = variant === "control";
  return {
    persona: isControl ? "unknown" : inferred,
    inferred,
    confident: isControl ? false : confident,
    variant: variant ?? "adapted",
  };
}

// ─── Signal collection ───────────────────────────────────────────────────────
let referrerAppliedThisSession = false;

/** Apply the first-touch referrer signal once per session, before first paint so the
 * initial content order already reflects it (no visible reflow). */
export function useReferrerSignalOnce(): void {
  const addSignal = usePersonaStore((s) => s.addSignal);
  useLayoutEffect(() => {
    if (referrerAppliedThisSession) return;
    let already: boolean;
    try {
      already = sessionStorage.getItem("wl_persona_ref") === "1";
    } catch {
      already = false;
    }
    referrerAppliedThisSession = true;
    if (already) return;
    const hit = classifyReferrer(
      typeof document !== "undefined" ? document.referrer : "",
      typeof location !== "undefined" ? location.search : "",
    );
    if (hit) addSignal({ [hit.persona]: REFERRER_WEIGHT });
    try {
      sessionStorage.setItem("wl_persona_ref", "1");
    } catch {
      /* private mode — session guard best-effort */
    }
  }, [addSignal]);
}

function isPersonaTopic(v: string | null): v is Persona {
  return !!v && (PERSONAS as string[]).includes(v);
}

interface DwellState {
  topic: Persona;
  visibleSince: number;
  accruedMs: number;
  reportedMs: number;
}

/**
 * Site-wide dwell tracking. Scans the current page for elements marked with a
 * `data-topic="<persona>"` attribute and credits that topic for the time each is on
 * screen — so ANY page opts in by adding an attribute, with no imports or hooks. Rescans
 * on navigation (pathKey change). Reports in ~second increments; refines for next load.
 */
export function useAutoDwell(pathKey: string): void {
  const addSignal = usePersonaStore((s) => s.addSignal);

  useEffect(() => {
    if (
      typeof IntersectionObserver === "undefined" ||
      typeof document === "undefined"
    ) {
      return;
    }

    const states = new Map<Element, DwellState>();

    const flushOne = (el: Element, st: DwellState) => {
      if (st.visibleSince) {
        st.accruedMs += Date.now() - st.visibleSince;
        st.visibleSince = 0;
      }
      const unreportedSec = Math.floor((st.accruedMs - st.reportedMs) / 1000);
      if (unreportedSec >= 1) {
        addSignal({ [st.topic]: unreportedSec * DWELL_WEIGHT_PER_SEC });
        st.reportedMs += unreportedSec * 1000;
        if (isCentered(el)) st.visibleSince = Date.now();
      }
    };
    const flushAll = () => states.forEach((st, el) => flushOne(el, st));

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const st = states.get(entry.target);
          if (!st) continue;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            if (!st.visibleSince) st.visibleSince = Date.now();
          } else {
            flushOne(entry.target, st);
          }
        }
      },
      { threshold: [0, 0.5, 1] },
    );

    // Scan after the new route has painted.
    const scanId = window.setTimeout(() => {
      document.querySelectorAll("[data-topic]").forEach((el) => {
        const topic = el.getAttribute("data-topic");
        if (!isPersonaTopic(topic)) return;
        states.set(el, {
          topic,
          visibleSince: 0,
          accruedMs: 0,
          reportedMs: 0,
        });
        io.observe(el);
      });
    }, 50);

    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushAll();
    };
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(flushAll, 4000);

    return () => {
      window.clearTimeout(scanId);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      flushAll();
      io.disconnect();
    };
  }, [pathKey, addSignal]);
}

function isCentered(el: Element): boolean {
  const r = el.getBoundingClientRect();
  const mid = window.innerHeight * 0.5;
  return r.top < mid && r.bottom > mid;
}

/**
 * Credit the current route's topic for active time on the page. Reading a mapped page
 * (e.g. /protocol -> developer) is itself a signal, captured with zero per-page code.
 * Only counts foreground time and stops crediting when the tab is hidden.
 */
export function useRouteDwell(pathname: string): void {
  const addSignal = usePersonaStore((s) => s.addSignal);

  useEffect(() => {
    const topic = routeTopicFor(pathname);
    if (!topic || typeof document === "undefined") return;

    let activeSince = document.visibilityState === "visible" ? Date.now() : 0;
    let accruedMs = 0;
    let reportedMs = 0;

    const flush = () => {
      if (activeSince) {
        accruedMs += Date.now() - activeSince;
        activeSince = document.visibilityState === "visible" ? Date.now() : 0;
      }
      const unreportedSec = Math.floor((accruedMs - reportedMs) / 1000);
      if (unreportedSec >= 1) {
        addSignal({ [topic]: unreportedSec * DWELL_WEIGHT_PER_SEC });
        reportedMs += unreportedSec * 1000;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        flush();
        activeSince = 0;
      } else if (!activeSince) {
        activeSince = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(flush, 4000);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [pathname, addSignal]);
}
